'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const router = useRouter();
  const [form, setForm] = useState({
    full_name: '',
    username: '',
    email: '',
    phone: '',
    country_flag: '',
    password: '',
    confirm_password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name,
          username: form.username,
          email: form.email,
          phone: form.phone,
          country_flag: form.country_flag,
          password: form.password,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Registration failed.');
        return;
      }

      window.location.href = '/dashboard';
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex items-center gap-2.5 mb-6" suppressHydrationWarning>
            <img src="/assets/images/app_logo.png" alt="HiyaPrime" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: "50%" }} suppressHydrationWarning />
            <span className="font-bold text-lg tracking-tight text-foreground">
              HiyaPrime<span className="text-primary">Trading</span>
            </span>
          </Link>
          <h1 className="text-2xl font-bold text-foreground">Create your account</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Your full name is permanent and used for account verification.
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-xl">
          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-primary/10 border border-primary/30 text-primary text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Full Name <span className="text-primary">*</span>
                <span className="ml-2 text-xs text-muted-foreground font-normal">(cannot be changed)</span>
              </label>
              <input
                type="text"
                name="full_name"
                value={form.full_name}
                onChange={handleChange}
                placeholder="Your legal full name"
                required
                suppressHydrationWarning
                className="w-full px-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Username <span className="text-primary">*</span>
              </label>
              <input
                type="text"
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="Choose a username"
                required
                autoComplete="username"
                suppressHydrationWarning
                className="w-full px-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Email Address <span className="text-primary">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                autoComplete="email"
                suppressHydrationWarning
                className="w-full px-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Phone Number <span className="text-primary">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+1 234 567 8900"
                required
                suppressHydrationWarning
                className="w-full px-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Country <span className="text-primary">*</span>
              </label>
              <select
                name="country_flag"
                value={form.country_flag}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 rounded-lg bg-muted border border-border text-foreground focus:outline-none focus:border-primary transition-colors text-sm"
              >
                
<option value="">Select your country</option>
<option value="🇰🇪">Kenya 🇰🇪</option>
<option value="🇳🇬">Nigeria 🇳🇬</option>
<option value="🇿🇦">South Africa 🇿🇦</option>
<option value="🇨🇦">Canada 🇨🇦</option>
<option value="🇦🇺">Australia 🇦🇺</option>
<option value="🇩🇪">Germany 🇩🇪</option>
<option value="🇫🇷">France 🇫🇷</option>
<option value="🇮🇳">India 🇮🇳</option>
<option value="🇧🇷">Brazil 🇧🇷</option>
<option value="🇯🇵">Japan 🇯🇵</option>
<option value="🇸🇦">Saudi Arabia 🇸🇦</option>
<option value="🇦🇪">United Arab Emirates 🇦🇪</option>
<option value="🇲🇽">Mexico 🇲🇽</option>
<option value="🇪🇸">Spain 🇪🇸</option>
<option value="🇮🇹">Italy 🇮🇹</option>
<option value="🇳🇱">Netherlands 🇳🇱</option>
<option value="🇷🇴">Romania 🇷🇴</option>
<option value="🇨🇴">Colombia 🇨🇴</option>
<option value="🇨🇲">Cameroon 🇨🇲</option>
<option value="🇬🇦">Gabon 🇬🇦</option>
<option value="🇨🇩">Congo DRC 🇨🇩</option>
<option value="🇸🇳">Senegal 🇸🇳</option>
<option value="🇨🇮">Cote D'Ivoire 🇨🇮</option>
<option value="🇧🇫">Burkina Faso 🇧🇫</option>
<option value="🇲🇱">Mali 🇲🇱</option>
<option value="🇧🇯">Benin 🇧🇯</option>
<option value="🇹🇬">Togo 🇹🇬</option>
<option value="🇹🇿">Tanzania 🇹🇿</option>
<option value="🇺🇬">Uganda 🇺🇬</option>
<option value="🇳🇪">Niger 🇳🇪</option>
<option value="🇷🇼">Rwanda 🇷🇼</option>
<option value="🇨🇬">Congo Brazzaville 🇨🇬</option>
<option value="🇬🇳">Guinea Conakry 🇬🇳</option>
<option value="🇬🇭">Ghana 🇬🇭</option>

              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Password <span className="text-primary">*</span>
              </label>
              <div className="relative" suppressHydrationWarning>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Min. 8 characters"
                  required
                  autoComplete="new-password"
                  suppressHydrationWarning
                  className="w-full px-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Confirm Password <span className="text-primary">*</span>
              </label>
              <div className="relative" suppressHydrationWarning>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  name="confirm_password"
                  value={form.confirm_password}
                  onChange={handleChange}
                  placeholder="Repeat your password"
                  required
                  autoComplete="new-password"
                  suppressHydrationWarning
                  className="w-full px-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 text-sm font-semibold mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link href="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
